package com.elan.edubridge.shared.data.repository

import com.elan.edubridge.shared.data.datasource.EduConnectDataSource
import com.elan.edubridge.shared.domain.model.AcademyApplication
import com.elan.edubridge.shared.domain.model.AcademyData
import com.elan.edubridge.shared.domain.model.Assignment
import com.elan.edubridge.shared.domain.model.AssignmentSubmission
import com.elan.edubridge.shared.domain.model.AttendanceEvent
import com.elan.edubridge.shared.domain.model.AttendanceSummary
import com.elan.edubridge.shared.domain.model.AttendanceTimelineEntry
import com.elan.edubridge.shared.domain.model.CalendarEvent
import com.elan.edubridge.shared.domain.model.ClassData
import com.elan.edubridge.shared.domain.model.ContentReport
import com.elan.edubridge.shared.domain.model.ContentStatus
import com.elan.edubridge.shared.domain.model.DailyRosterEntry
import com.elan.edubridge.shared.domain.model.GuardianLink
import com.elan.edubridge.shared.domain.model.JoinApplication
import com.elan.edubridge.shared.domain.model.JoinApplicationStatus
import com.elan.edubridge.shared.domain.model.NoticeData
import com.elan.edubridge.shared.domain.model.PostComment
import com.elan.edubridge.shared.domain.model.PostData
import com.elan.edubridge.shared.domain.model.ReadResult
import com.elan.edubridge.shared.domain.model.StudentData
import com.elan.edubridge.shared.domain.model.TodayClass
import com.elan.edubridge.shared.domain.repository.EduConnectRepository
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.callbackFlow

/**
 * **소프트 삭제 툼스톤을 걸러 내는 유일한 지점이다.**
 *
 * 공지·게시글·댓글은 삭제돼도 문서가 남고(`status: DELETED`, 제목·본문은 비워진다) rules는 여전히
 * ACTIVE 멤버 전원에게 read를 열어 준다. 데이터소스 구현마다 필터를 걸면 셋 중 하나(그리고 M3의
 * Swift 구현)가 빠지는 날 빈 카드가 목록에 섞이므로, 필터는 여기 한 곳에만 둔다.
 */
class EduConnectRepositoryImpl(
    private val dataSource: EduConnectDataSource,
) : EduConnectRepository {
    override suspend fun academies(academyIds: List<String>): List<AcademyData> =
        dataSource.academies(academyIds)

    override suspend fun classes(academyId: String): List<ClassData> =
        dataSource.classes(academyId)

    override suspend fun students(academyId: String): List<StudentData> =
        dataSource.students(academyId)

    // 아래 6개는 필터·정렬을 직접 갖지 않고 `*Result`에 위임한다(#30). 두 경로가 각자 필터를
    // 들고 있던 동안 실제로 `academyEventsResult`의 정렬이 빠졌고, 그건 화면에서 보이지 않는
    // 종류의 어긋남이었다 — 규칙을 한 벌만 두면 어긋날 자리가 사라진다.

    override suspend fun notices(academyId: String): List<NoticeData> =
        noticesResult(academyId).items

    /**
     * 최신 작성순. 서버는 정렬 보장을 하지 않고 Firestore 기본 순서는 문서 id순(자동 id라 사실상
     * 무작위)이라, `myAcademyApplications`와 같은 이유로 클라이언트에서 정렬한다 —
     * `orderBy`를 붙이면 `status` 필터와 묶여 복합 인덱스가 하나 더 필요해진다.
     */
    override suspend fun posts(academyId: String): List<PostData> = postsResult(academyId).items

    override suspend fun post(academyId: String, postId: String): PostData? =
        postResult(academyId, postId).item

    /** 스레드는 오래된 댓글이 위다 — 목록과 정렬 방향이 반대인 것은 의도된 것이다. */
    override suspend fun postComments(academyId: String, postId: String): List<PostComment> =
        postCommentsResult(academyId, postId).items

    /**
     * 시작 시각 오름차순. 캘린더도 리스트도 시간 순으로 읽으므로 목록(최신순)과 방향이 반대인
     * 것은 의도된 것이다. 정렬을 서버 `orderBy`로 옮기지 않는 이유는 `posts`와 다르다 —
     * 여기서는 `startAt` 범위 조건이 이미 그 필드의 정렬을 강제하므로 굳이 붙일 이유가 없고,
     * 툼스톤을 걷어낸 뒤의 순서만 여기서 확정하면 된다.
     */
    override suspend fun academyEvents(
        academyId: String,
        startAt: String,
        endAt: String,
    ): List<CalendarEvent> = academyEventsResult(academyId, startAt, endAt).items

    override suspend fun personalEvents(
        userId: String,
        startAt: String,
        endAt: String,
    ): List<CalendarEvent> = personalEventsResult(userId, startAt, endAt).items

    override suspend fun studentSchedule(
        academyId: String,
        studentUserId: String,
    ): List<ClassData> = dataSource.studentSchedule(academyId, studentUserId)

    override suspend fun studentAssignments(
        academyId: String,
        studentUserId: String,
    ): List<Assignment> = studentAssignmentsResult(academyId, studentUserId).items

    override suspend fun todayClasses(academyId: String, serviceDate: String?): List<TodayClass> =
        dataSource.todayClasses(academyId, serviceDate)

    override suspend fun attendanceSummary(
        academyId: String,
        serviceDate: String?,
    ): AttendanceSummary = dataSource.attendanceSummary(academyId, serviceDate)

    override suspend fun dailyRoster(
        academyId: String,
        serviceDate: String?,
        classId: String?,
    ): List<DailyRosterEntry> = dataSource.dailyRoster(academyId, serviceDate, classId)

    override suspend fun myAcademyApplications(applicantUid: String): List<AcademyApplication> =
        dataSource.myAcademyApplications(applicantUid)

    /**
     * 신고에는 툼스톤이 없다 — 종결된 신고는 지워지는 게 아니라 `RESOLVED`로 남는다.
     * 그래서 다른 목록과 달리 여기서 ACTIVE 필터를 걸지 않고, 무엇을 보여줄지는
     * [openOnly]로 호출자가 정한다.
     */
    override suspend fun contentReports(
        academyId: String,
        openOnly: Boolean,
    ): List<ContentReport> =
        dataSource.contentReports(academyId, openOnly)

    /**
     * 가입 신청에도 툼스톤이 없다 — 반려·취소·차단은 삭제가 아니라 상태다([contentReports]와
     * 같은 이유로 ACTIVE 필터를 걸지 않는다). 무엇을 보여줄지는 [status]로 호출자가 정한다.
     */
    override suspend fun joinApplications(
        academyId: String,
        status: JoinApplicationStatus,
    ): List<JoinApplication> =
        dataSource.joinApplications(academyId, status)

    override suspend fun myJoinApplications(userId: String): List<JoinApplication> =
        dataSource.myJoinApplications(userId)

    // ---- 실패를 구분하는 읽기 (#30, 단계 1) ----
    //
    // 원본과 **같은 필터·같은 정렬**을 적용한다. 툼스톤 필터가 이 클래스에서 끝난다는 규칙이
    // 새 경로에서 깨지면 구현이 넷으로 늘어난 만큼 빈 카드가 새는 곳도 늘어난다.

    /** 실패를 보존한 채 목록만 변환한다 — 필터가 실패를 지우지 않게 하는 유일한 지점. */
    private fun <T, R> ReadResult<T>.mapItems(transform: (List<T>) -> List<R>): ReadResult<R> =
        ReadResult(items = transform(items), failure = failure)

    override suspend fun academiesResult(academyIds: List<String>): ReadResult<AcademyData> =
        dataSource.academiesResult(academyIds)

    override suspend fun classesResult(academyId: String): ReadResult<ClassData> =
        dataSource.classesResult(academyId)

    override suspend fun studentsResult(academyId: String): ReadResult<StudentData> =
        dataSource.studentsResult(academyId)

    override suspend fun noticesResult(academyId: String): ReadResult<NoticeData> =
        dataSource.noticesResult(academyId)
            // 데이터소스는 정렬하지 않는다(복합 인덱스 회피, 각 플랫폼 KDoc 참고). 문서 id 순으로 오면 새 공지가
            // 옛 공지들 사이에 끼어 보인다 — Z Flip 실측(2026-09-09). 게시글과 같은 자리에서 최신순으로 맞춘다.
            .mapItems { list -> list.filter { it.status == ContentStatus.ACTIVE }.sortedByDescending { it.date } }

    override suspend fun postsResult(academyId: String): ReadResult<PostData> =
        dataSource.postsResult(academyId).mapItems { list ->
            list.filter { it.status == ContentStatus.ACTIVE }.sortedByDescending { it.createdAt }
        }

    override suspend fun postResult(academyId: String, postId: String): ReadResult<PostData> =
        dataSource.postResult(academyId, postId)
            .mapItems { list -> list.filter { it.status == ContentStatus.ACTIVE } }

    override suspend fun postCommentsResult(
        academyId: String,
        postId: String,
    ): ReadResult<PostComment> =
        dataSource.postCommentsResult(academyId, postId).mapItems { list ->
            list.filter { it.status == ContentStatus.ACTIVE }.sortedBy { it.createdAt }
        }

    override suspend fun studentScheduleResult(
        academyId: String,
        studentUserId: String,
    ): ReadResult<ClassData> = dataSource.studentScheduleResult(academyId, studentUserId)

    override suspend fun studentAssignmentsResult(
        academyId: String,
        studentUserId: String,
    ): ReadResult<Assignment> =
        dataSource.studentAssignmentsResult(academyId, studentUserId).mapItems { list ->
            list.filter { it.status == ContentStatus.ACTIVE }.sortedBy { it.dueDate }
        }

    override suspend fun assignmentsResult(academyId: String): ReadResult<Assignment> =
        dataSource.assignmentsResult(academyId).mapItems { list ->
            list.filter { it.status == ContentStatus.ACTIVE }.sortedBy { it.dueDate }
        }

    /** 이름 순. 미제출 학생을 채워 넣는 것은 명부를 아는 화면의 몫이다. */
    override suspend fun assignmentSubmissionsResult(
        academyId: String,
        assignmentId: String,
    ): ReadResult<AssignmentSubmission> =
        dataSource.assignmentSubmissionsResult(academyId, assignmentId)
            .mapItems { list -> list.sortedBy { it.studentName } }

    override suspend fun pendingAcademyApplicationsResult(): ReadResult<AcademyApplication> =
        dataSource.pendingAcademyApplicationsResult()

    override suspend fun myAcademyApplicationsResult(
        applicantUid: String,
    ): ReadResult<AcademyApplication> = dataSource.myAcademyApplicationsResult(applicantUid)

    override suspend fun contentReportsResult(
        academyId: String,
        openOnly: Boolean,
    ): ReadResult<ContentReport> = dataSource.contentReportsResult(academyId, openOnly)

    override suspend fun joinApplicationsResult(
        academyId: String,
        status: JoinApplicationStatus,
    ): ReadResult<JoinApplication> = dataSource.joinApplicationsResult(academyId, status)

    override suspend fun myJoinApplicationsResult(userId: String): ReadResult<JoinApplication> =
        dataSource.myJoinApplicationsResult(userId)

    override suspend fun todayClassesResult(
        academyId: String,
        serviceDate: String?,
    ): ReadResult<TodayClass> = dataSource.todayClassesResult(academyId, serviceDate)

    /** 툼스톤 필터 + 시작 시각 오름차순 — 원본 [academyEvents]와 **같은 필터·같은 정렬**이다. */
    override suspend fun academyEventsResult(
        academyId: String,
        startAt: String,
        endAt: String,
    ): ReadResult<CalendarEvent> = dataSource.academyEventsResult(academyId, startAt, endAt)
        .mapItems { list ->
            list.filter { it.status == ContentStatus.ACTIVE }.sortedBy { it.startAt }
        }

    override suspend fun personalEventsResult(
        userId: String,
        startAt: String,
        endAt: String,
    ): ReadResult<CalendarEvent> = dataSource.personalEventsResult(userId, startAt, endAt)
        .mapItems { list ->
            list.filter { it.status == ContentStatus.ACTIVE }.sortedBy { it.startAt }
        }

    override suspend fun attendanceRecordsRange(
        academyId: String,
        startDate: String,
        endDate: String,
        classId: String?,
        studentId: String?,
    ): List<DailyRosterEntry> =
        dataSource.attendanceRecordsRange(academyId, startDate, endDate, classId, studentId)

    override suspend fun myTimeline(
        userId: String,
        academyId: String?,
        subjectStudentId: String?,
        limit: Int,
    ): List<AttendanceTimelineEntry> =
        dataSource.myTimeline(userId, academyId, subjectStudentId, limit)

    override suspend fun guardianLinks(guardianUserId: String): List<GuardianLink> =
        dataSource.guardianLinks(guardianUserId)

    override suspend fun studentGuardianLinks(
        academyId: String,
        studentId: String,
    ): List<GuardianLink> = dataSource.studentGuardianLinks(academyId, studentId)

    override suspend fun assignments(academyId: String): List<Assignment> =
        assignmentsResult(academyId).items

    override suspend fun assignmentSubmissions(
        academyId: String,
        assignmentId: String,
    ): List<AssignmentSubmission> = assignmentSubmissionsResult(academyId, assignmentId).items

    override suspend fun pendingAcademyApplications(): List<AcademyApplication> =
        dataSource.pendingAcademyApplications()

    /**
     * 콜백 포트를 다시 `Flow`로 되돌린다.
     *
     * datasource가 `Flow`를 버린 것은 Swift가 그 인터페이스를 **구현**해야 하기 때문이고, 그
     * 제약이 repository 위쪽까지 올라갈 이유는 없다 — Android·웹 화면은 지금까지처럼 이 `Flow`를
     * collect한다. 어댑터를 여기 한 곳에 두는 덕분에 계약 변경 범위가 datasource 구현 셋으로
     * 갇힌다.
     */
    override fun attendanceEventStream(
        academyId: String,
        limit: Int,
    ): Flow<List<AttendanceEvent>> = callbackFlow {
        val subscription = dataSource.observeAttendanceEvents(
            academyId = academyId,
            limit = limit,
            onEach = { trySend(it) },
            onComplete = { close() },
        )
        awaitClose { subscription.cancel() }
    }
}
